import { chainPullRequestsByBaseBranch } from "@afar-research/afar";
import type { EnvironmentPullRequestRow } from "@afar-research/client-runtime/state/pull-requests";

/**
 * The workspace list as lines, with a pull request built on another sitting under it.
 *
 * A listing already carries both branch names for every row, so the chain costs no read at all —
 * unlike a host's own stack, which has to be asked for one pull request at a time. What it can
 * see is therefore limited to what the list is showing: a stack whose bottom layer is closed and
 * filtered out reads as a shorter stack, which is what the reader is looking at anyway.
 */

export interface PullRequestListChainLine {
  readonly row: EnvironmentPullRequestRow;
  /** 0 for a pull request on the base branch; each layer built on it steps in by one. */
  readonly depth: number;
  /** Set on the bottom layer of a chain of more than one, so that row can name the whole chain. */
  readonly chainSize: number | null;
}

function rowKey(row: EnvironmentPullRequestRow): string {
  return `${row.environmentId}:${row.projectId}:${row.host}/${row.repository}#${row.number}`;
}

function activityAt(row: EnvironmentPullRequestRow): number {
  const milliseconds = Date.parse(row.updatedAt);
  return Number.isNaN(milliseconds) ? 0 : milliseconds;
}

export function pullRequestListChainLines(
  rows: ReadonlyArray<EnvironmentPullRequestRow>,
): ReadonlyArray<PullRequestListChainLine> {
  const chains = chainPullRequestsByBaseBranch(rows, {
    key: rowKey,
    // Two Environments can hold one project id, and two hosts one repository name.
    scope: (row) => `${row.environmentId}:${row.projectId}:${row.host}/${row.repository}`,
    headBranch: (row) => row.headRefName,
    baseBranch: (row) => row.baseRefName,
  });
  // A chain sorts by its most recent layer, so a fresh push anywhere floats the whole chain up
  // and the layer to review first stays at the bottom of the indent.
  return [...chains]
    .sort((left, right) => Math.max(...right.map(activityAt)) - Math.max(...left.map(activityAt)))
    .flatMap((layers) =>
      layers.map((row, depth) => ({
        row,
        depth,
        chainSize: depth === 0 && layers.length > 1 ? layers.length : null,
      })),
    );
}
